import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useMovie, useUpdateMovie } from '@/hooks/useMovies';
import { CreateMovieRequest } from '@/types/model';
import MovieForm from '@/components/forms/MovieForm';
import Button from '@/components/ui/Button';
import {useTranslation} from '@/hooks';

const EditMovie = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: movie, isLoading, isError, error } = useMovie(id!);
  const updateMovieMutation = useUpdateMovie();
  const { t } = useTranslation();

  const handleSuccess = async (data: CreateMovieRequest) => {
    try {
      await updateMovieMutation.mutateAsync({ id: id!, data });
      navigate(-1);
    } catch (error) {
      console.error('Update movie error:', error);
      throw error;
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="flex items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              <span className="text-gray-600">Cargando película...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="outline"
            onClick={() => navigate('/content/movies')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a Películas
          </Button>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="text-center">
              <div className="text-red-600 text-lg font-medium mb-2">
                Error al cargar la película
              </div>
              <p className="text-gray-600 mb-4">
                {error?.message || 'No se pudo cargar la información de la película.'}
              </p>
              <Button onClick={() => navigate('/content/movies')}>
                Volver a la lista
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="outline"
            onClick={() => navigate('/content/movies')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a Películas
          </Button>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="text-center">
              <div className="text-gray-600 text-lg font-medium mb-2">
                Película no encontrada
              </div>
              <p className="text-gray-500 mb-4">
                La película que está buscando no existe o ha sido eliminada.
              </p>
              <Button onClick={() => navigate('/content/movies')}>
                Volver a la lista
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={handleCancel}
            className="text-indigo-600 hover:text-indigo-500 text-sm font-medium mb-4 flex items-center"
          >
            ← {t.buttons.cancel}
          </button>
        </div>
        <MovieForm
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          initialData={movie}
          isEdit={true}
          isSubmitting={updateMovieMutation.isPending}
        />
      </div>
    </div>
  );
};

export default EditMovie;
